/**
 * Lightweight fetch wrapper with JWT attach and 401 handling.
 * All paths are relative — CRA proxy (frontend/package.json "proxy") forwards
 * /api/* to the backend during dev. In production, frontend + API share an origin.
 */

import { clearStashedAdminToken } from './auth/impersonation';

const TOKEN_KEY = 'auth_token';

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
  window.dispatchEvent(new StorageEvent('storage', { key: TOKEN_KEY, newValue: token }));
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
  window.dispatchEvent(new StorageEvent('storage', { key: TOKEN_KEY, newValue: null }));
}

export class ApiError extends Error {
  constructor(public readonly status: number, public readonly code: string, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

interface ApiOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  /** When true, do NOT redirect on 401. Used by login flows that expect 401 on bad creds. */
  noRedirectOn401?: boolean;
}

export async function api<T = any>(path: string, opts: ApiOptions = {}): Promise<T> {
  const token = getToken();
  const headers: Record<string, string> = {};
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(path, {
    method: opts.method ?? 'GET',
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
  });

  let json: any = null;
  const text = await res.text();
  if (text) {
    try { json = JSON.parse(text); } catch { /* leave null */ }
  }

  if (!res.ok) {
    if (res.status === 401 && !opts.noRedirectOn401) {
      clearToken();
      clearStashedAdminToken();
      if (typeof window !== 'undefined') {
        const p = window.location.pathname;
        // Only hard-redirect from inside the app (dashboard / admin). A stale
        // token on a public marketing page (/, /blog, /pricing…) must not yank
        // the visitor to a sign-in screen.
        const inApp = p.startsWith('/dashboard') || p.startsWith('/admin');
        if (inApp && !p.startsWith('/admin/login')) {
          // Super-admin pages bounce to the operator portal, everything else to
          // the customer sign-in (kept consistent with AuthProvider.logout()).
          window.location.href = p.startsWith('/admin') ? '/admin/login' : '/login';
        }
      }
    }
    const code = json?.error?.code ?? `http_${res.status}`;
    const msg = json?.error?.message ?? `Request failed (${res.status})`;
    throw new ApiError(res.status, code, msg);
  }

  return json as T;
}
